import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Modal from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import './RegisterModal.css';

// Import icons (install react-icons if you haven't: npm install react-icons)
import {
    FaLinkedin,
    FaTwitter,
    FaFacebook,
    FaInstagram,
    FaGithub,
    FaYoutube,
    FaTiktok
} from 'react-icons/fa';

const RegisterModal = ({ isOpen, onClose, onBackToLogin, onRegistrationSuccess, isStandalonePage = false }) => {
    const { register } = useAuth();
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    if (!isOpen) return null;

    const initialValues = {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        user_type: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        phone_number: '',
        bio: '',
        website: '',
        social_links: {
            linkedin: '',
            twitter: '',
            facebook: '',
            instagram: '',
            github: '',
            youtube: '',
            tiktok: ''
        },
        agreed_to_terms: false
    };

    const validationSchema = Yup.object({
        username: Yup.string()
            .matches(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, hyphens, and underscores')
            .required('Username is required'),
        email: Yup.string().email('Invalid email address').required('Email is required'),
        password: Yup.string()
            .min(8, 'Password must be at least 8 characters')
            .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
            .matches(/[0-9]/, 'Password must contain at least one number')
            .required('Password is required'),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Please confirm your password'),
        user_type: Yup.string()
            .oneOf(['content_creator', 'general_user'], 'Please select a user type')
            .required('Please select a user type'),
        // Conditional validation for address fields
        address_line_1: Yup.string().when('user_type', {
            is: 'content_creator',
            then: (schema) => schema.required('Address Line 1 is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        city: Yup.string().when('user_type', {
            is: 'content_creator',
            then: (schema) => schema.required('City is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        postal_code: Yup.string().when('user_type', {
            is: 'content_creator',
            then: (schema) => schema.required('Postal Code is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        phone_number: Yup.string()
            .when('user_type', {
                is: 'content_creator',
                then: (schema) => schema
                    .required('Phone number is required')
                    .test('uk-phone', function(value) {
                        const { path, createError } = this;

                        if (!value) return true;

                        // Remove spaces, dashes, parentheses
                        const cleaned = value.replace(/[\s\-()]/g, '');

                        // UK phone number patterns
                        const patterns = {
                            mobile: /^((\+44|0)7\d{9})$/,
                            london: /^((\+44|0)20\d{8})$/,
                            standard: /^((\+44|0)1\d{8,9})$/,
                            nonGeo: /^((\+44|0)3\d{9})$/
                        };

                        // Check against patterns
                        if (patterns.mobile.test(cleaned)) {
                            return true; // Valid mobile
                        }
                        if (patterns.london.test(cleaned)) {
                            return true; // Valid London number
                        }
                        if (patterns.standard.test(cleaned)) {
                            return true; // Valid standard landline
                        }
                        if (patterns.nonGeo.test(cleaned)) {
                            return true; // Valid non-geographic
                        }

                        // Custom error message based on what might be wrong
                        if (cleaned.length < 10) {
                            return createError({
                                path,
                                message: 'UK phone numbers should have at least 10 digits'
                            });
                        }
                        if (cleaned.length > 13) {
                            return createError({
                                path,
                                message: 'UK phone numbers should have no more than 13 digits'
                            });
                        }

                        return createError({
                            path,
                            message: 'Please enter a valid UK phone number (e.g., 07123 456789 or +44 7123 456789)'
                        });
                    })
                    .transform((value) => {
                        if (!value) return value;

                        // Format the number nicely for display/storage
                        let cleaned = value.replace(/[\s\-()]/g, '');

                        // Convert to +44 format
                        if (cleaned.startsWith('0')) {
                            cleaned = '+44' + cleaned.substring(1);
                        } else if (cleaned.startsWith('44') && !cleaned.startsWith('+44')) {
                            cleaned = '+' + cleaned;
                        } else if (!cleaned.startsWith('+')) {
                            cleaned = '+44' + cleaned;
                        }

                        return cleaned;
                    }),
                otherwise: (schema) => schema.notRequired(),
            }),
        website: Yup.string()
            .when('user_type', {
                is: 'content_creator',
                then: (schema) => schema
                    .optional()
                    .test('is-valid-url', 'Please enter a valid website URL', (value) => {
                        if (!value) return true; // Optional field

                        // Check if it's a valid URL format
                        try {
                            // Add https:// if missing for validation
                            let urlToTest = value;
                            if (!value.startsWith('http://') && !value.startsWith('https://')) {
                                urlToTest = 'https://' + value;
                            }

                            const url = new URL(urlToTest);

                            // Check for valid domain (has at least one dot)
                            const domainParts = url.hostname.split('.');
                            if (domainParts.length < 2) return false;

                            // Check for valid TLD (at least 2 chars after last dot)
                            const lastPart = domainParts[domainParts.length - 1];
                            if (lastPart.length < 2) return false;

                            return true;
                        } catch {
                            return false;
                        }
                    })
                    .transform((value) => {
                        if (!value) return value;

                        // Add https:// if no protocol specified
                        if (!value.startsWith('http://') && !value.startsWith('https://')) {
                            return 'https://' + value;
                        }
                        return value;
                    }),
                otherwise: (schema) => schema.notRequired()
            }),

        // Bio validation
        bio: Yup.string()
            .when('user_type', {
                is: 'content_creator',
                then: (schema) => schema
                    .optional()
                    .max(500, 'Bio must be at most 500 characters')
                    .min(10, 'Bio must be at least 10 characters if provided')
                    .matches(
                        /^[a-zA-Z0-9\s\-_,.;:[\]()!?'"&@#+]*$/,
                        'Bio can only contain letters, numbers, and basic punctuation'
                    ),
                otherwise: (schema) => schema.notRequired()
            }),

        // Social links validation
        social_links: Yup.object()
            .when('user_type', {
                is: 'content_creator',
                then: (schema) => schema
                    .optional()
                    .shape({
                        linkedin: Yup.string()
                            .optional()
                            .test('linkedin-url', 'Please enter a valid LinkedIn URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/.test(value);
                            }),
                        twitter: Yup.string()
                            .optional()
                            .test('twitter-url', 'Please enter a valid Twitter/X URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/?$/.test(value);
                            }),
                        facebook: Yup.string()
                            .optional()
                            .test('facebook-url', 'Please enter a valid Facebook URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9.]+\/?$/.test(value);
                            }),
                        instagram: Yup.string()
                            .optional()
                            .test('instagram-url', 'Please enter a valid Instagram URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/.test(value);
                            }),
                        github: Yup.string()
                            .optional()
                            .test('github-url', 'Please enter a valid GitHub URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?github\.com\/[a-zA-Z0-9-]+\/?$/.test(value);
                            }),
                        youtube: Yup.string()
                            .optional()
                            .test('youtube-url', 'Please enter a valid YouTube URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?youtube\.com\/(@[a-zA-Z0-9_]+|channel\/[a-zA-Z0-9_-]+|c\/[a-zA-Z0-9_-]+)/.test(value);
                            }),
                        tiktok: Yup.string()
                            .optional()
                            .test('tiktok-url', 'Please enter a valid TikTok URL', (value) => {
                                if (!value) return true;
                                return /^(https?:\/\/)?(www\.)?tiktok\.com\/@[a-zA-Z0-9_.]+\/?$/.test(value);
                            })
                    })
                    .test('at-least-one', 'Please provide at least one social media link', (value) => {
                        if (!value) return true;
                        return Object.values(value).some(url => url && url.trim() !== '');
                    }),
                otherwise: (schema) => schema.notRequired()
            }),
        
        // Terms and Conditions agreement
        agreed_to_terms: Yup.boolean()
            .oneOf([true], 'You must agree to the Terms and Conditions to register')
            .required('You must agree to the Terms and Conditions to register')
    });

    const handleSubmit = async (values, { setSubmitting, setStatus }) => {
        try {
            // Filter out empty social links
            const filteredSocialLinks = {};
            if (values.social_links) {
                Object.entries(values.social_links).forEach(([key, value]) => {
                    if (value && value.trim() !== '') {
                        filteredSocialLinks[key] = value;
                    }
                });
            }

            const userData = {
                first_name: values.first_name,
                last_name: values.last_name,
                username: values.username,
                email: values.email,
                password: values.password,
                user_type: values.user_type,
                address_line_1: values.address_line_1 || null,
                address_line_2: values.address_line_2 || null,
                city: values.city || null,
                postal_code: values.postal_code || null,
                phone_number: values.phone_number || null,
                bio: values.bio || null,
                website: values.website || null,
                social_links: Object.keys(filteredSocialLinks).length > 0 ? filteredSocialLinks : null,
            };

            await register(userData);
            setRegistrationSuccess(true);
            setStatus({ success: 'Registration successful! Please check your email to verify your account.' });

            if (onRegistrationSuccess) onRegistrationSuccess(userData);
            if (!isStandalonePage) setTimeout(onClose, 3000);
        } catch (error) {
            setStatus({ error: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return '';
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        if (strength <= 2) return 'weak';
        if (strength === 3) return 'medium';
        return 'strong';
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Create an Account"
            size="large"
            closeOnBackdrop={false}
            closeOnEsc={false}
        >
            <RegisterFormContent
                registrationSuccess={registrationSuccess}
                initialValues={initialValues}
                validationSchema={validationSchema}
                handleSubmit={handleSubmit}
                getPasswordStrength={getPasswordStrength}
                onBackToLogin={onBackToLogin}
            />
        </Modal>
    );
};

const SocialLinksFields = ({ values, errors, touched }) => {
    const socialFields = [
        { name: 'linkedin', icon: FaLinkedin, color: '#0077b5', placeholder: 'username or full URL' },
        { name: 'twitter', icon: FaTwitter, color: '#1DA1F2', placeholder: 'username or full URL' },
        { name: 'facebook', icon: FaFacebook, color: '#4267B2', placeholder: 'username or full URL' },
        { name: 'instagram', icon: FaInstagram, color: '#E4405F', placeholder: 'username or full URL' },
        { name: 'github', icon: FaGithub, color: '#333', placeholder: 'username or full URL' },
        { name: 'youtube', icon: FaYoutube, color: '#FF0000', placeholder: 'channel URL' },
        { name: 'tiktok', icon: FaTiktok, color: '#000000', placeholder: '@username' }
    ];

    return (
        <div className="social-links-section">
            <h4 className="section-subtitle">Social Media Links (at least one recommended)</h4>
            <div className="social-links-grid">
                {socialFields.map(({ name, icon: Icon, color, placeholder }) => (
                    <div key={name} className="social-link-field">
                        <label htmlFor={`social_links.${name}`} className="social-label">
                            <Icon style={{ color }} className="social-icon" />
                            <span className="capitalize">{name}</span>
                        </label>
                        <Field
                            type="text"
                            name={`social_links.${name}`}
                            id={`social_links.${name}`}
                            className="form-control"
                            placeholder={placeholder}
                        />
                        {errors.social_links?.[name] && touched.social_links?.[name] && (
                            <div className="error-message">{errors.social_links[name]}</div>
                        )}
                    </div>
                ))}
            </div>
            {errors.social_links && typeof errors.social_links === 'string' && (
                <div className="error-message">{errors.social_links}</div>
            )}
        </div>
    );
};

const RegisterFormContent = ({
                                 registrationSuccess,
                                 initialValues,
                                 validationSchema,
                                 handleSubmit,
                                 getPasswordStrength,
                                 onBackToLogin,
                             }) => {
    return (
        <>
            {registrationSuccess ? (
                <div className="success-message">
                    Registration successful! Please check your email to verify your account.
                </div>
            ) : (
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ isSubmitting, values, errors, touched, status }) => (
                        <Form className="register-form">
                            {/* Global status messages */}
                            {status?.error && <div className="form-alert error">{status.error}</div>}
                            {status?.success && <div className="form-alert success">{status.success}</div>}

                            {/* User Type (top) */}
                            <div className="form-section">
                                <h4 className="section-subtitle">Account Type</h4>
                                <div className={`form-group ${errors.user_type && touched.user_type ? 'has-error' : ''}`}>
                                    <div className="user-type-row">
                                        <label className="radio-option">
                                            <Field type="radio" name="user_type" value="content_creator" />
                                            <span className="radio-label">Content Creator</span>
                                        </label>
                                        <label className="radio-option">
                                            <Field type="radio" name="user_type" value="general_user" />
                                            <span className="radio-label">General User</span>
                                        </label>
                                    </div>
                                    {errors.user_type && touched.user_type &&
                                        <div className="error-message">{errors.user_type}</div>}
                                </div>
                            </div>

                            {/* Personal Information */}
                            <div className="form-section">
                                <h4 className="section-subtitle">Personal Information</h4>
                                <div className="form-row">
                                    <div className={`form-group ${errors.first_name && touched.first_name ? 'has-error' : ''}`}>
                                        <label htmlFor="first_name">First Name</label>
                                        <Field type="text" name="first_name" id="first_name" className="form-control" />
                                        {errors.first_name && touched.first_name &&
                                            <div className="error-message">{errors.first_name}</div>}
                                    </div>

                                    <div className={`form-group ${errors.last_name && touched.last_name ? 'has-error' : ''}`}>
                                        <label htmlFor="last_name">Last Name</label>
                                        <Field type="text" name="last_name" id="last_name" className="form-control" />
                                        {errors.last_name && touched.last_name &&
                                            <div className="error-message">{errors.last_name}</div>}
                                    </div>
                                </div>

                                <div className={`form-group ${errors.username && touched.username ? 'has-error' : ''}`}>
                                    <label htmlFor="username">Username</label>
                                    <Field type="text" name="username" id="username" className="form-control" />
                                    {errors.username && touched.username &&
                                        <div className="error-message">{errors.username}</div>}
                                </div>

                                <div className={`form-group ${errors.email && touched.email ? 'has-error' : ''}`}>
                                    <label htmlFor="email">Email</label>
                                    <Field type="email" name="email" id="email" className="form-control" />
                                    {errors.email && touched.email &&
                                        <div className="error-message">{errors.email}</div>}
                                </div>
                            </div>

                            {/* Password Section */}
                            <div className="form-section">
                                <h4 className="section-subtitle">Password</h4>
                                <div className="form-row">
                                    <div className={`form-group ${errors.password && touched.password ? 'has-error' : ''}`}>
                                        <label htmlFor="password">Password</label>
                                        <Field type="password" name="password" id="password" className="form-control" />
                                        {values.password && (
                                            <div className={`password-strength ${getPasswordStrength(values.password)}`}>
                                                Strength: {getPasswordStrength(values.password)}
                                            </div>
                                        )}
                                        {errors.password && touched.password &&
                                            <div className="error-message">{errors.password}</div>}
                                    </div>

                                    <div className={`form-group ${errors.confirm_password && touched.confirm_password ? 'has-error' : ''}`}>
                                        <label htmlFor="confirm_password">Confirm Password</label>
                                        <Field type="password" name="confirm_password" id="confirm_password" className="form-control" />
                                        {errors.confirm_password && touched.confirm_password &&
                                            <div className="error-message">{errors.confirm_password}</div>}
                                    </div>
                                </div>
                            </div>

                            {/* Content Creator Fields */}
                            {values.user_type === 'content_creator' && (
                                <>
                                    {/* Address Section */}
                                    <div className="form-section">
                                        <h4 className="section-subtitle">Address Information</h4>
                                        <div className={`form-group ${errors.address_line_1 && touched.address_line_1 ? 'has-error' : ''}`}>
                                            <label htmlFor="address_line_1">Address Line 1 *</label>
                                            <Field type="text" name="address_line_1" id="address_line_1" className="form-control" />
                                            {errors.address_line_1 && touched.address_line_1 &&
                                                <div className="error-message">{errors.address_line_1}</div>}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="address_line_2">Address Line 2 (optional)</label>
                                            <Field type="text" name="address_line_2" id="address_line_2" className="form-control" />
                                        </div>

                                        <div className="form-row">
                                            <div className={`form-group ${errors.city && touched.city ? 'has-error' : ''}`}>
                                                <label htmlFor="city">City *</label>
                                                <Field type="text" name="city" id="city" className="form-control" />
                                                {errors.city && touched.city &&
                                                    <div className="error-message">{errors.city}</div>}
                                            </div>

                                            <div className={`form-group ${errors.postal_code && touched.postal_code ? 'has-error' : ''}`}>
                                                <label htmlFor="postal_code">Postal Code *</label>
                                                <Field type="text" name="postal_code" id="postal_code" className="form-control" />
                                                {errors.postal_code && touched.postal_code &&
                                                    <div className="error-message">{errors.postal_code}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact & Bio Section */}
                                    <div className="form-section">
                                        <h4 className="section-subtitle">Contact & Bio</h4>

                                        <div className={`form-group ${errors.phone_number && touched.phone_number ? 'has-error' : ''}`}>
                                            <label htmlFor="phone_number">Phone Number *</label>
                                            <Field type="text" name="phone_number" id="phone_number" className="form-control" />
                                            {errors.phone_number && touched.phone_number &&
                                                <div className="error-message">{errors.phone_number}</div>}
                                        </div>

                                        <div className={`form-group ${errors.website && touched.website ? 'has-error' : ''}`}>
                                            <label htmlFor="website">Website (optional)</label>
                                            <Field type="text" name="website" id="website" className="form-control" />
                                            {errors.website && touched.website &&
                                                <div className="error-message">{errors.website}</div>}
                                        </div>

                                        <div className={`form-group ${errors.bio && touched.bio ? 'has-error' : ''}`}>
                                            <label htmlFor="bio">Bio (optional)</label>
                                            <Field as="textarea" name="bio" id="bio" className="form-control" rows="4" />
                                            {values.bio && (
                                                <div className="char-count">
                                                    {values.bio.length}/500
                                                </div>
                                            )}
                                            {errors.bio && touched.bio &&
                                                <div className="error-message">{errors.bio}</div>}
                                        </div>
                                    </div>

                                    {/* Social Links Section */}
                                    <SocialLinksFields values={values} errors={errors} touched={touched} />
                                </>
                            )}

                            {/* Terms & Conditions Agreement */}
                            <div className="terms-agreement-section">
                                <label className={`terms-checkbox-label ${errors.agreed_to_terms && touched.agreed_to_terms ? 'has-error' : ''}`}>
                                    <Field 
                                        type="checkbox" 
                                        name="agreed_to_terms" 
                                        className="terms-checkbox"
                                    />
                                    <span>
                                        I agree to the{' '}
                                        <a 
                                            href="/terms-and-conditions" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="terms-link"
                                        >
                                            Terms and Conditions
                                        </a>
                                    </span>
                                </label>
                                {errors.agreed_to_terms && touched.agreed_to_terms && (
                                    <div className="error-message">{errors.agreed_to_terms}</div>
                                )}
                            </div>

                            <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-block">
                                {isSubmitting ? 'Creating Account...' : 'Create Account'}
                            </button>

                            <div className="form-footer">
                                <p>
                                    Already have an account?{' '}
                                    <button type="button" className="link-button" onClick={onBackToLogin}>
                                        Back to Login
                                    </button>
                                </p>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

export default RegisterModal;